import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

export function SelectDriverFilter({ setEmployee }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const buttonRef = useRef(null);

  useEffect(() => {
    if (selectedEmployee) {
      setEmployee(selectedEmployee);
    } else {
      setEmployee('');
    }
  }, [selectedEmployee]);

  const fetchEmployees = useCallback(async (nome) => {
    if (!nome || nome.length < 1) {
      setEmployees([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://radar.tlpservicos.com.br/api/funcionarios/filtro?nome=${encodeURIComponent(
          nome
        )}`
      );

      if (response.ok) {
        const data = await response.json();
        setEmployees(Array.isArray(data) ? data : []);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error('Erro ao buscar funcionários:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchEmployees(searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchEmployees]);

  const formatEmployeeDisplay = (employee) => {
    return `${employee.nome} - filial: ${employee.filial} - cpf: ${employee.cpf}`;
  };

  const handleToggleOpen = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
    setIsOpen(false);
    setSearchTerm('');
  };

  const clearSelection = () => {
    setSelectedEmployee(null);
    setSearchTerm('');
    setEmployees([]);
  };

  return (
    <>
      <div className="w-full max-w-2xl mx-auto">
        <div className="relative">
          <div
            ref={buttonRef}
            onClick={handleToggleOpen}
            className="inline-flex items-center text-gray-900 justify-between w-full h-9 px-3 text-sm bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
          >
            <span className="truncate">
              {selectedEmployee
                ? formatEmployeeDisplay(selectedEmployee)
                : 'Selecione um condutor...'}
            </span>
            <div className="flex items-center space-x-2">
              <ChevronDown
                size={16}
                className={`transform transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown com posicionamento fixo */}
      {isOpen && (
        <div
          className="fixed bg-white border border-gray-300 rounded-md shadow-lg z-[9999]"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Digite o nome do funcionário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-gray-900 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-4 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                Buscando...
              </div>
            )}

            {!searchTerm && !loading && (
              <div className="py-4 text-center text-sm text-gray-500">
                Digite pelo menos 1 caractere para buscar
              </div>
            )}

            {searchTerm && !loading && employees.length === 0 && (
              <div className="py-4 text-center text-sm text-gray-500">
                Nenhum funcionário encontrado
              </div>
            )}

            {employees.map((employee) => (
              <div
                key={employee.id}
                onClick={() => handleSelectEmployee(employee)}
                className="px-4 py-3 text-sm text-gray-900 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium">{employee.nome}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Filial: {employee.filial} • CPF: {employee.cpf} • Estado:{' '}
                  {employee.estado}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overlay para fechar o dropdown */}
      {isOpen && (
        <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
      )}
    </>
  );
}
